// The firmware editor: the left half of the page once a robot is in the scene.
//
// It opens on the welcome sketch for the board the robot was built with -- the
// same file `src/controller.rs` bakes into the wasm, imported as text -- so the
// code on screen is the code the board booted with. Editing and running it sends
// the sketch to that board's compile service and re-flashes the simulated chip
// with what comes back.
//
// Monaco is created on first use rather than at startup: it is the largest
// thing in the bundle, and until a robot exists there is nothing to flash it to.
// The pane and its resize handle stay hidden until then.
//
// One editor, pointed at the most recently added robot. Dropping a second robot
// re-points it (and its serial monitor) rather than opening another pane.
//
// A robot folder may carry its own sketches, in which case they are what opens
// instead -- see `readDroppedSketches`, and the picker in the bar that switches
// between them. They are only shown, never flashed: the board still boots the
// firmware baked into the wasm, and running the sketch on screen is the user's
// move.

import * as monaco from "monaco-editor";
import { AddedRobot, DroppedFolder, InterfaceHybrid } from "simulator";

import esp32WelcomeSketch from "../../firmware/esp32s3/welcome/welcome.ino";
import welcomeSketch from "../../firmware/welcome/welcome.ino";
import {
  ARDUINO_NANO,
  CompileError,
  compileSketch,
  isEsp32,
} from "./compile";
import { cameraViewRotations } from "./camera";
import { downloadConfig, filesBySuffix, withViewRotations } from "./config";
import { showBuild, watchRobot } from "./monitor";

// Arduino keeps a sketch in a folder of its own name, so a robot folder that
// ships one has it a level or two down -- and a folder that ships several has
// them side by side down there, which is why every one of them is taken rather
// than only the shallowest. `four-legged/` is the case: a `calibrate/` beside a
// `quadruped_walk/`, and no reason for the page to guess which the user meant.
const SKETCH_SUFFIX = ".ino";

let editor: monaco.editor.IStandaloneCodeEditor | undefined;
let hybrid: InterfaceHybrid | undefined;
// The robot "Run" re-flashes: its index, and the pose it started in.
let robot: AddedRobot | undefined;
// The hardware and wiring that robot was built from, as a file -- what "Save
// config" downloads. Written afresh each time the pane is pointed at a robot, so
// a robot rebuilt from an edited plan saves the edited answers.
let config: string | undefined;
// What "Edit config" does, which is the page's business rather than this
// module's: re-opening the configuration panel on the plan the robot was built
// from and building it again is a whole drop's tail, and it ends by pointing
// this pane at the rebuilt robot. `undefined` for a robot with no plan behind
// it, which hides the button.
let reconfigure: (() => void) | undefined;
// The sketches the drop brought, in the order the picker offers them.
let sketches: OpenSketch[] = [];

// The firmware a board of this kind boots into, as text.
//
// Both are imported from the very files `src/controller.rs` bakes in, so the
// editor cannot drift from what is actually running. A robot with no board gets
// the Nano's, which is only ever a starting point to type over: there is nothing
// to flash it to.
function welcomeFor(board: string | undefined): string {
  return isEsp32(board) ? esp32WelcomeSketch : welcomeSketch;
}

function element<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`index.html has no #${id}`);
  }
  return found as T;
}

// A sketch that came with the robot, and where in the drop it was found -- the
// path is worth keeping, because it is the only thing that tells the user which
// of their files ended up on screen.
export interface DroppedSketch {
  path: string;
  source: string;
}

// One of those once it is open in the editor. A monaco model each, rather than
// one buffer set to whichever sketch is showing: switching to another sketch and
// back has to come back to what was typed into this one, undo history and all,
// and a model is exactly that much state.
interface OpenSketch {
  path: string;
  model: monaco.editor.ITextModel;
}

// Every sketch in a dropped folder, shallowest first.
//
// Read while the folder is still in JS's hands: `planRobotFromFolder` consumes
// it, so this has to happen at drop time even though the editor is not opened
// until the robot is actually added.
export function readDroppedSketches(folder: DroppedFolder): DroppedSketch[] {
  const found: DroppedSketch[] = [];
  for (const path of filesBySuffix(folder, SKETCH_SUFFIX)) {
    const source = folder.text(path);
    if (source !== undefined) {
      found.push({ path, source });
    }
  }
  return found;
}

// What to call each sketch in the picker: its file name, which is the name the
// user gave it and the one Arduino names its folder after. Two sketches in a
// drop can still share one, and a label that identifies nothing is worse than a
// long one, so in that case every entry falls back to its whole path.
function labelsFor(paths: string[]): string[] {
  const names = paths.map((path) => path.split("/").pop() ?? path);
  return new Set(names).size === names.length ? names : paths;
}

// Put the drop's sketches in the picker and open the first.
//
// The previous drop's models are disposed once the editor is off them -- which
// is safe here and only here, since this runs exactly when the editor is being
// pointed at a sketch of this drop's instead.
function openSketches(dropped: DroppedSketch[]) {
  const previous = sketches;
  sketches = dropped.map(({ path, source }) => ({
    path,
    model: monaco.editor.createModel(source, "cpp"),
  }));

  const labels = labelsFor(sketches.map(({ path }) => path));
  const picker = element<HTMLSelectElement>("editor-sketch");
  picker.replaceChildren(
    ...sketches.map(({ path }, index) => {
      const option = new Option(labels[index] ?? path, `${index}`);
      // The whole path, for the drop where the names alone are enough to tell
      // them apart but not enough to say where each came from.
      option.title = path;
      return option;
    }),
  );
  // The tab names the baked-in firmware until a drop brings something else to
  // show; from here the picker is the answer to which file is on screen.
  picker.hidden = false;
  element("editor-file").hidden = true;

  showSketch(0);
  for (const { model } of previous) {
    model.dispose();
  }
}

// Show the sketch at `index`, leaving whatever is in the others untouched.
function showSketch(index: number) {
  const sketch = sketches[index];
  if (!editor || !sketch) {
    return;
  }
  editor.setModel(sketch.model);
  const picker = element<HTMLSelectElement>("editor-sketch");
  picker.value = `${index}`;
  picker.title = sketch.path;
}

// Show the editor for the robot just added, and start watching its serial
// output. The sketch it holds is otherwise kept across robots: someone who has
// written code and then drops another robot means to run it on the new one --
// but a robot that brought its own sketches is an explicit statement about what
// that robot runs, so `dropped` replaces whatever is in the editor. A rebuild
// through "Edit config" passes none for exactly that reason: the robot is new
// but the code being written for it is not.
//
// `onEditConfig` is what that button does. It belongs to the page rather than
// here -- it re-opens the configuration panel and runs a whole drop's tail --
// and it comes back through this function, since the last thing it does is
// point the pane at the robot it rebuilt.
export function editFirmwareOf(
  scene: InterfaceHybrid,
  added: AddedRobot,
  name: string,
  configuration: string,
  dropped: DroppedSketch[] = [],
  onEditConfig?: () => void,
) {
  // The robot this pane was pointed at has just been replaced and taken out of
  // the scene, and nothing else holds its handle: `addTo` hands one over per
  // build, and the editor is what keeps it for the life of the robot.
  if (robot !== added) {
    robot?.free();
  }
  hybrid = scene;
  robot = added;
  config = configuration;
  reconfigure = onEditConfig;

  element("editor-pane").hidden = false;
  element("editor-resize").hidden = false;
  element("editor-edit-config").hidden = onEditConfig === undefined;
  // The board is part of the answer to "what am I writing code for" now that
  // there is more than one, and the sketch on screen differs between them.
  element("editor-target").textContent = added.microcontroller
    ? `${name} \u00b7 ${added.microcontroller}`
    : name;
  // The 3D view is sized from its container, and giving up half the page is a
  // layout change no window event announces.
  window.dispatchEvent(new Event("resize"));

  if (!editor) {
    editor = monaco.editor.create(element("editor"), {
      value: welcomeFor(added.microcontroller),
      language: "cpp",
      theme: "vs-dark",
      automaticLayout: true,
      fontSize: 13,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
    });
    // Wired here, not at import time: this module is pulled in from index.ts,
    // which runs before anything guarantees the pane is in the document.
    element("editor-run").addEventListener("click", () => {
      void run();
    });
    // Saves the robot the pane is currently pointed at, the same as "Run" does.
    // Dropping that file back into the folder is what makes the next drop skip
    // the configuration panel. The camera views' turns go in as they stand
    // now, since they are changed from the camera panel after the build.
    element("editor-save").addEventListener("click", () => {
      if (config !== undefined) {
        downloadConfig(withViewRotations(config, cameraViewRotations()));
      }
    });
    // Re-opens the configuration panel on the robot the pane is pointed at.
    // Read off the module variable rather than closed over: this listener is
    // wired once, and the robot it is about changes under it.
    element("editor-edit-config").addEventListener("click", () => {
      reconfigure?.();
    });
    element("editor-resize").addEventListener("pointerdown", startResize);
    // Which of the drop's sketches is on screen. Nothing is flashed by picking
    // one -- the board keeps running what it booted with, exactly as it does
    // for the sketch that opened first.
    element("editor-sketch").addEventListener("change", (event) => {
      showSketch(Number((event.target as HTMLSelectElement).value));
    });
  }
  if (dropped.length > 0) {
    openSketches(dropped);
  }
  watchRobot(scene, added.index);

  // Loading a sketch does not flash it: the board booted on the firmware baked
  // into the wasm, so what is on screen and what is running are two different
  // programs until Run is pressed. Said in the build panel, which is where the
  // answer to "what is the board running" already lives.
  if (dropped.length > 0) {
    const opened = dropped[0]?.path;
    showBuild(
      (dropped.length === 1
        ? `Loaded ${opened} from the robot folder.\n`
        : `Loaded ${dropped.length} sketches from the robot folder, ` +
          `showing ${opened}.\n`) +
        `The board is still running the firmware it booted with -- ` +
        `press Run to compile and flash this sketch.`,
    );
  }
}

// Compile what is in the editor and run it on the robot.
//
// The robot is put back where it started first, so a run begins from the pose
// its hardware was configured in rather than wherever the last one left it --
// the same firmware then behaves the same way twice. `restart` rather than
// `hybrid.reset()`, which would set every joint to zero and drop the robot
// through the floor. Either re-flash keeps the servos and their wiring; only the
// code changes.
//
// The two boards are flashed through different calls because they are flashed
// with different things -- Intel HEX text for the Nano, an application image and
// a symbol table for the S3. `compileSketch` tags what it returns with the board
// it built for, so the branch below cannot pick the wrong one.
async function run() {
  // Read both before the await, so what gets flashed is the sketch as it was
  // when Run was pressed, onto the robot it was pressed for.
  const scene = hybrid;
  const target = robot;
  const sketch = editor?.getValue();
  if (!scene || !target || sketch === undefined) {
    return;
  }

  const board = target.microcontroller;
  const button = element<HTMLButtonElement>("editor-run");

  // A robot built without a microcontroller has a null controller and no
  // firmware; there is nothing to compile for and nothing to flash.
  if (board === undefined) {
    showBuild(
      "This robot was built without a microcontroller, so there is nothing " +
        "to flash. Drop it again and choose a board to run code on it.",
      true,
    );
    return;
  }

  button.disabled = true;
  button.textContent = "Compiling...";
  showBuild(`Compiling for the ${board}...`);

  try {
    const compiled = await compileSketch(sketch, board, target.psram);
    // Restart before flashing either board: the robot goes back to the pose its
    // hardware was configured in, so the same firmware behaves the same way
    // twice.
    target.restart(scene);
    // Either S3: compared against the Nano because that is what narrows the
    // union to the application image.
    if (compiled.board !== ARDUINO_NANO) {
      showBuild(
        `Compiled: ${compiled.appBin.length} bytes of application image.\n${compiled.log}`.trim(),
      );
      scene.reboot_esp32_controller(
        target.index,
        compiled.appBin,
        compiled.symbols,
      );
    } else {
      showBuild(
        `Compiled: ${compiled.hex.length} bytes of hex.\n${compiled.log}`.trim(),
      );
      scene.reboot_code_controller(target.index, compiled.hex);
    }
  } catch (error) {
    const message =
      error instanceof CompileError ? error.message : String(error);
    showBuild(message, true);
    console.error("firmware build failed", error);
  } finally {
    button.disabled = false;
    button.textContent = "Run";
  }
}

// Drag the divider to trade width between the editor and the 3D view. Both
// panes are flex children, so setting the editor's basis is enough; the view
// takes what is left and has to be told to re-measure.
function startResize(event: PointerEvent) {
  event.preventDefault();
  const pane = element("editor-pane");

  const drag = (move: PointerEvent) => {
    const width = move.clientX - pane.getBoundingClientRect().left;
    const max = window.innerWidth * 0.8;
    if (width > 120 && width < max) {
      pane.style.flexBasis = `${width}px`;
      window.dispatchEvent(new Event("resize"));
    }
  };
  const stop = () => {
    document.removeEventListener("pointermove", drag);
    document.removeEventListener("pointerup", stop);
  };

  document.addEventListener("pointermove", drag);
  document.addEventListener("pointerup", stop);
}
